import { z } from 'zod';
import { LocalizedSectionTitle, PortfolioLayoutHeader } from '@mews-commander/ui';
import { LocalizedText, createZodFormResolver, getBase64, SubmitHandler, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, IconName, Stack, StackItem } from '@optimus-web/core';
import { ExcelProcessingType } from '@mews-commander/data-access';
import { DropzoneField } from '@mews-commander/form';
import { useDownloadExcelProcessing, useUploadExcelProcessing } from '../../../../queries/useExcelProcessing';

const schema = z.object({
    files: z.instanceof(File).array().min(1),
});

interface WithExcelProcessingType {
    type: ExcelProcessingType;
}

const useChannelManagerMappingFormControls = ({ type }: WithExcelProcessingType) => {
    const { mutate, isPending } = useUploadExcelProcessing();
    const resolver = createZodFormResolver(schema);

    const onSubmit: SubmitHandler<{ files: File[] }> = async (files) => {
        const base64Data = await getBase64(files.files[0]);
        mutate({ type, data: base64Data });
    };

    const { submit, renderFormContent } = useFormControls({
        defaultValues: { files: [] },
        onSubmit,
        resolver,
    });

    return { submit, renderFormContent, isLoading: isPending };
};

interface FileUploadSectionProps extends WithExcelProcessingType {
    title: Mews.TextKey;
}

export const FileUploadSection = ({ title, type }: FileUploadSectionProps) => {
    const { renderFormContent, submit, isLoading } = useChannelManagerMappingFormControls({ type });
    const downloadExcelProcessing = useDownloadExcelProcessing();

    const onDownload = () => {
        downloadExcelProcessing.mutate({ type });
    };

    return (
        <>
            <PortfolioLayoutHeader
                title={<LocalizedSectionTitle textKey={title} />}
                rightSide={
                    <Button icon={IconName.Summary} onClick={onDownload} size="medium" type="submit">
                        <LocalizedText textKey={TextKey.DownloadTemplate} />
                    </Button>
                }
            />
            {renderFormContent(
                <Stack vertical spacing="200" itemAlignment="end">
                    <StackItem grow>
                        <DropzoneField
                            uploadFilesText={<LocalizedText textKey={TextKey.DragAndDropFileToImport} />}
                            name="files"
                            label={<LocalizedText textKey={TextKey.ImportFile} />}
                            accept=".xlsx,.xls"
                            required
                        />
                    </StackItem>
                    <StackItem>
                        <Button
                            onClick={submit}
                            size="medium"
                            variant="primary"
                            loading={isLoading}
                            disabled={isLoading}
                            type="submit"
                        >
                            <LocalizedText textKey={TextKey.Import} />
                        </Button>
                    </StackItem>
                </Stack>
            )}
        </>
    );
};
